import { useState, useEffect } from 'react';
import { Icon } from '../../icons';
import { Btn, Alert } from '../../components/ui';
import { getNotifications, markAsRead, markAllAsRead } from '../../api/notifications';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

export default function NotificationsScreen({ onNav }) {
  const { language = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[language]?.notifications?.[key] || translations.en?.notifications?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const typeColor = {
    danger: "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20",
    warn: "border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20",
    info: "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20",
    success: "border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20"
  };
  const iconColor = { danger: "text-red-500", warn: "text-amber-500", info: "text-blue-500", success: "text-emerald-500" };
  const loadData = async () => {
    try {
      setLoading(true);
      const res = await getNotifications();
      if (res.data && res.data.data) {
        setItems(res.data.data);
        setUnreadCount(res.data.unread_count || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleMarkAsRead = async (n) => {
    try {
      if (!n.read) {
        await markAsRead(n.id);
        setItems(items.map(item => item.id === n.id ? { ...item, read: true } : item));
        const newCount = Math.max(0, unreadCount - 1);
        setUnreadCount(newCount);
        window.dispatchEvent(new CustomEvent('notifications-read', { detail: { count: newCount } }));
      }
      
      if (n.action_url && onNav) {
        let path = n.action_url.replace(/^\//, ''); // remove leading slash
        // Map backend action_url to frontend screen IDs
        if (path === 'admin/service-providers') path = 'admin-providers';
        else if (path === 'member/service-providers') path = 'service-providers';
        else if (path.startsWith('member/notices/')) path = 'notices';
        
        onNav(path);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
      setItems(items.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      window.dispatchEvent(new CustomEvent('notifications-read', { detail: { count: 0 } }));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="fade-up max-w-2xl mx-auto space-y-4">
      {/* Modern Header Container */}
      <div className="flex justify-between items-center bg-white dark:bg-slate-900 border border-border dark:border-slate-800 p-4 rounded-2xl shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="bell_ring" size={18} className="text-primary" />
          </div>
          <div>
            <div className="text-sm font-black text-ink dark:text-slate-100 uppercase tracking-wider">
              Notifications
            </div>
            <div className="text-xs font-semibold text-muted dark:text-slate-400 mt-0.5">
              {unreadCount === 0 ? "You're all caught up!" : `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}`}
            </div>
          </div>
        </div>
        {unreadCount > 0 && (
          <Btn xs variant="primary" onClick={handleMarkAllRead} className="shadow-lg shadow-primary/20">
            {t("btn_mark_all_read")}
          </Btn>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="skeleton h-20 w-full rounded-2xl" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-slate-900 border border-border dark:border-slate-800 rounded-2xl shadow-sm">
          <div className="w-16 h-16 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
            <Icon name="notifications" size={24} className="text-slate-300 dark:text-slate-600" />
          </div>
          <div className="text-sm font-semibold text-ink dark:text-slate-300">{t("text_no_notifications")}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(n => {
            const isDanger = n.type === 'danger';
            const isUnread = !n.read;
            return (
              <div key={n.id} onClick={() => handleMarkAsRead(n)}
                className={`relative flex items-center gap-4 p-4 rounded-2xl border transition-all duration-300 cursor-pointer hover:-translate-y-0.5 hover:shadow-xl ${isUnread
                    ? isDanger
                      ? 'bg-red-50/50 dark:bg-red-950/20 border-red-200 dark:border-red-900/50 shadow-sm'
                      : 'bg-white dark:bg-slate-900 border-primary/20 dark:border-primary/20 shadow-sm'
                    : 'bg-slate-50/50 dark:bg-slate-900/40 border-slate-100 dark:border-slate-800 opacity-80'
                  }`}>

                {/* Danger pulsing indicator for unread emergencies */}
                {isUnread && isDanger && (
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-red-400 to-red-600 rounded-2xl opacity-20 blur-sm animate-pulse" style={{ zIndex: -1 }} />
                )}

                <div className={`w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center border-2 ${isUnread
                    ? isDanger ? 'bg-red-100 border-red-200 text-red-600 dark:bg-red-900/50 dark:border-red-800 dark:text-red-400' : 'bg-primary/10 border-primary/20 text-primary dark:bg-primary/20 dark:border-primary/30'
                    : 'bg-white border-slate-100 text-slate-400 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-500'
                  }`}>
                  <Icon name={n.icon || (isDanger ? 'alert' : 'notifications')} size={20} />
                </div>

                <div className="flex-1 min-w-0 py-1">
                  <div className={`text-sm mb-1 line-clamp-1 ${isUnread ? "text-ink dark:text-white font-bold" : "text-slate-600 dark:text-slate-300 font-semibold"}`}>
                    {n.title}
                  </div>
                  {n.body && <div className="text-xs text-muted dark:text-slate-400 line-clamp-2 leading-relaxed mb-1.5">{n.body}</div>}
                  <div className="text-sm font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">{n.time}</div>
                </div>

                {isUnread && (
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isDanger ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]' : 'bg-primary shadow-[0_0_8px_rgba(79,70,229,0.6)]'}`} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
